import { useDeferredValue, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Search, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface AlunoSearchResult {
  id: string;
  nome: string;
  matricula: string;
  turma_id: string;
}

export function GlobalStudentSearch() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [results, setResults] = useState<AlunoSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen(current => !current);
      }
    };

    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, []);

  useEffect(() => {
    const term = deferredSearch.trim().replace(/[,%()]/g, "");
    if (!open || term.length < 2 || !user?.escola_id) {
      setResults([]);
      setError(null);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setError(null);

      const { data, error: queryError } = await supabase
        .from("alunos")
        .select("id, nome, matricula, turma_id")
        .eq("escola_id", user.escola_id)
        .or(`nome.ilike.%${term}%,matricula.ilike.%${term}%`)
        .order("nome")
        .limit(12);

      if (cancelled) return;

      if (queryError) {
        console.error("Erro na busca global:", queryError);
        setError("Não foi possível pesquisar agora.");
        setResults([]);
      } else {
        setResults((data || []) as AlunoSearchResult[]);
      }
      setLoading(false);
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [deferredSearch, open, user?.escola_id]);

  const goToStudent = (aluno: AlunoSearchResult) => {
    setOpen(false);
    setSearch("");
    navigate(`/turmas/${aluno.turma_id}/alunos/${aluno.id}`);
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="h-10 w-full justify-between border-slate-200 bg-white px-3 text-slate-500 shadow-none"
        onClick={() => setOpen(true)}
      >
        <span className="flex items-center gap-2">
          <Search className="h-4 w-4" aria-hidden="true" />
          Buscar aluno
        </span>
        <kbd className="hidden rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium sm:inline">
          Ctrl K
        </kbd>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="top-[12%] max-w-xl translate-y-0 sm:top-[18%]">
          <DialogHeader>
            <DialogTitle>Buscar aluno</DialogTitle>
            <DialogDescription>
              Pesquise por nome ou matrícula e abra o perfil sem navegar entre turmas.
            </DialogDescription>
          </DialogHeader>

          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
              aria-hidden="true"
            />
            <Input
              autoFocus
              type="search"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Digite pelo menos 2 caracteres"
              className="pl-9"
              aria-label="Nome ou matrícula do aluno"
            />
          </div>

          <div className="max-h-[50dvh] min-h-28 overflow-y-auto" aria-live="polite">
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Pesquisando...
              </div>
            ) : error ? (
              <p className="py-10 text-center text-sm text-red-700" role="alert">{error}</p>
            ) : deferredSearch.trim().length < 2 ? (
              <p className="py-10 text-center text-sm text-slate-500">
                A busca começará após o segundo caractere.
              </p>
            ) : results.length === 0 ? (
              <p className="py-10 text-center text-sm text-slate-500">
                Nenhum aluno encontrado.
              </p>
            ) : (
              <div className="space-y-1">
                {results.map(aluno => (
                  <button
                    key={aluno.id}
                    type="button"
                    className="flex min-h-14 w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors hover:bg-slate-50 focus-visible:bg-slate-50"
                    onClick={() => goToStudent(aluno)}
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-indigo-700">
                      <UserRound className="h-4 w-4" aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-900">{aluno.nome}</p>
                      <p className="text-xs text-slate-500">Matrícula {aluno.matricula}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
